import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, MapPin, Star, ImageSquare, Flag, ShieldCheck, CaretLeft, CaretRight } from "@phosphor-icons/react";
import { countriesApi, toAbsolute } from "@/lib/apiClient";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import PhotoLightbox from "@/components/PhotoLightbox";
import { safetyColor } from "@/components/ReviewsPanel";
import { PASSPORT_DESTINATION_PLACEHOLDER } from "@/constants/destinationImage";

function timeAgo(iso) {
    if (!iso) return "";
    const d = new Date(iso);
    const s = Math.round((Date.now() - d.getTime()) / 1000);
    if (s < 60) return `${s}s`;
    const m = Math.round(s / 60);
    if (m < 60) return `${m}m`;
    const h = Math.round(m / 60);
    if (h < 24) return `${h}h`;
    return `${Math.round(h / 24)}d`;
}

const countryDrawerCache = new Map();

export default function CountryDrawer({ country, open, onClose }) {
    const { t } = useTranslation();
    const [data, setData] = useState(null);
    const [tab, setTab] = useState("stamps");
    const [lightboxPhotos, setLightboxPhotos] = useState([]);
    const [lightboxOpen, setLightboxOpen] = useState(false);

    useEffect(() => {
        if (!open || !country) return;
        const cached = countryDrawerCache.get(country);
        if (cached) {
            setData(cached);
            countriesApi.detail(country).then((res) => {
                countryDrawerCache.set(country, res);
                setData(res);
            }).catch(() => {});
            return;
        }
        setData(null);
        countriesApi.detail(country).then((res) => {
            countryDrawerCache.set(country, res);
            setData(res);
        }).catch(() => {});
    }, [open, country]);

    return (
        <AnimatePresence>
            {open && country && (
                <>
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                        className="fixed inset-0 z-[85]"
                        style={{ background: "rgba(5,10,17,0.7)", backdropFilter: "blur(10px)" }}
                        onClick={onClose}
                        data-testid="country-drawer"
                    />
                    <motion.div onClick={(e) => e.stopPropagation()}
                        initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
                        transition={{ type: "spring", damping: 30, stiffness: 240 }}
                        className="fixed top-0 right-0 h-full w-full max-w-2xl overflow-y-auto z-[90]"
                        style={{ background: "var(--navy)", borderLeft: "1px solid rgba(212,175,55,0.25)" }}
                    >
                        {/* Header */}
                        <div className="relative h-56 overflow-hidden">
                            <div
                                className={`absolute inset-0 transition-opacity duration-500 ${!data ? "animate-pulse" : ""}`}
                                style={{
                                    background: data?.hero_image && !data.hero_image.includes("placeholder")
                                        ? `linear-gradient(180deg, rgba(11,19,32,0.15) 0%, rgba(5,10,17,0.92) 100%), url('${data.hero_image}') center/cover`
                                        : "linear-gradient(140deg, #1a2a44 0%, #0A1320 100%)",
                                }}
                            />
                            {data?.hero_image && !data.hero_image.includes("placeholder") && data?.photographer_name && (
                                <a
                                    href={data.photographer_url || data.source_page_url || "https://unsplash.com/?utm_source=Passport&utm_medium=referral"}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="absolute top-5 left-6 text-[10px] text-white/75 hover:text-white transition-colors px-3 py-1 rounded-full flex items-center gap-1.5 z-10 glass"
                                    style={{ background: "rgba(5,10,17,0.65)", backdropFilter: "blur(8px)", border: "1px solid rgba(255,255,255,0.12)" }}
                                    title={`Photo by ${data.photographer_name} on Unsplash`}
                                >
                                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                                    <span>Photo by <strong className="font-medium text-white">{data.photographer_name}</strong> on Unsplash</span>
                                </a>
                            )}
                            <button onClick={onClose} className="absolute top-5 right-5 w-10 h-10 rounded-full glass grid place-items-center z-10" aria-label="Close" data-testid="country-drawer-close">
                                <X size={14} />
                            </button>
                            <div className="absolute bottom-6 left-6 right-6">
                                <div className="overline mb-2 flex items-center gap-2">
                                    <Flag size={12} weight="fill" /> {t("countryDrawer.overline")}
                                </div>
                                <h2 className="font-serif text-4xl md:text-5xl text-white tracking-tighter leading-none">{country}</h2>
                                {data && (
                                    <div className="flex items-center gap-6 mt-4 text-xs text-white/70">
                                        <span>{t("countryDrawer.stampsCount", { count: data.stamps?.length || 0 })}</span>
                                        <span>{t("countryDrawer.reviewsCount", { count: data.reviews?.length || 0 })}</span>
                                        <span>{t("countryDrawer.postsCount", { count: data.posts?.length || 0 })}</span>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Tabs */}
                        <div className="p-6 md:p-8">
                            <div className="flex gap-1 p-1 rounded-full w-max mb-6" style={{ background: "rgba(0,0,0,0.35)", border: "1px solid rgba(255,255,255,0.06)" }}>
                                {[
                                    { k: "stamps", label: t("countryDrawer.tabs.stamps"), icon: MapPin },
                                    { k: "reviews", label: t("countryDrawer.tabs.reviews"), icon: Star },
                                    { k: "posts", label: t("countryDrawer.tabs.posts"), icon: ImageSquare },
                                ].map((tItem) => {
                                    const Icon = tItem.icon;
                                    const active = tab === tItem.k;
                                    return (
                                        <button key={tItem.k} onClick={() => setTab(tItem.k)}
                                            className="px-4 py-2 rounded-full text-xs flex items-center gap-2 transition-colors"
                                            style={{ background: active ? "var(--gold)" : "transparent", color: active ? "var(--navy)" : "white" }}
                                            data-testid={`country-tab-${tItem.k}`}
                                        >
                                            <Icon size={12} weight={active ? "fill" : "regular"} />
                                            {tItem.label}
                                        </button>
                                    );
                                })}
                            </div>

                            {!data && <div className="text-white/50 text-sm">{t("countryDrawer.loading")}</div>}

                            {data && tab === "stamps" && (
                                <div className="grid grid-cols-2 gap-3">
                                    {data.stamps.length === 0 && <Empty text={t("countryDrawer.empty.stamps")} />}
                                    {data.stamps.map((s) => (
                                        <div key={s.id} className="rounded-2xl p-4 flex items-center gap-3" style={{ background: "var(--navy-deep)", border: "1px solid rgba(212,175,55,0.2)" }} data-testid={`country-stamp-${s.id}`}>
                                            <div className="w-10 h-10 rounded-full grid place-items-center shrink-0" style={{ background: "rgba(212,175,55,0.12)", border: "1px solid var(--gold)" }}>
                                                <MapPin size={14} color="#D4AF37" />
                                            </div>
                                            <div>
                                                <div className="font-serif text-lg text-white leading-tight">{s.city}</div>
                                                {s.note && (
                                                    s.note.startsWith("Auto-stamped") ? (
                                                        <div className="text-[10px] uppercase tracking-wider font-medium mt-1 inline-flex items-center gap-1 px-2 py-0.5 rounded-full" style={{ background: "rgba(212,175,55,0.12)", color: "#D4AF37", border: "1px solid rgba(212,175,55,0.3)" }}>
                                                            {t("countryDrawer.conciergeStamp")}
                                                        </div>
                                                    ) : (
                                                        <div className="text-white/50 text-xs mt-0.5">{s.note}</div>
                                                    )
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {data && tab === "reviews" && (
                            <div className="space-y-3">
                                {data.reviews.length === 0 && <Empty text={t("countryDrawer.empty.reviews")} />}
                                {data.reviews.map((r) => (
                                    <DrawerReviewCard key={r.id} r={r} />
                                ))}
                            </div>
                            )}

                                {data && tab === "posts" && (
                                    <div className="grid sm:grid-cols-2 gap-3">
                                        {data.posts.length === 0 && <Empty text={t("countryDrawer.empty.posts")} />}
                                        {data.posts.map((p) => (
                                            <div key={p.id} className="rounded-2xl overflow-hidden" style={{ background: "var(--navy-deep)", border: "1px solid rgba(255,255,255,0.06)" }} data-testid={`country-post-${p.id}`}>
                                                <img src={p.image_url} alt={p.location} className="w-full h-40 object-cover" />
                                                <div className="p-3 text-sm">
                                                    <span className="font-medium" style={{ color: "var(--gold)" }}>@{p.user_handle}</span>{" "}
                                                    <span className="text-white/80">{p.caption}</span>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </motion.div>
                </>
            )}
        </AnimatePresence>
    );
}

function DrawerReviewCard({ r }) {
    const photos = r.photos?.length ? r.photos : (r.photo_url ? [r.photo_url] : []);
    const [lightboxOpen, setLightboxOpen] = useState(false);
    const [photoIdx, setPhotoIdx] = useState(0);

    const nextPhoto = (e) => {
        e.stopPropagation();
        setPhotoIdx((prev) => (prev + 1) % photos.length);
    };

    const prevPhoto = (e) => {
        e.stopPropagation();
        setPhotoIdx((prev) => (prev - 1 + photos.length) % photos.length);
    };

    return (
        <div className="rounded-2xl overflow-hidden" style={{ background: "var(--navy-deep)", border: "1px solid rgba(255,255,255,0.06)" }} data-testid={`country-review-${r.id}`}>
            {photos.length > 0 && (
                <>
                <div className="relative cursor-pointer group aspect-[16/9] overflow-hidden bg-black" onClick={() => setLightboxOpen(true)}>
                    <div 
                        className="absolute inset-0 bg-center bg-cover blur-xl scale-110 opacity-50" 
                        style={{ backgroundImage: `url(${toAbsolute(photos[photoIdx])})` }} 
                    />
                    <img src={toAbsolute(photos[photoIdx])} alt={r.place_name} className="relative w-full h-full object-contain transition-opacity group-hover:opacity-90 z-10" />
                    {photos.length > 1 && (
                        <>
                            <button onClick={prevPhoto} className="absolute z-20 left-2 top-1/2 -translate-y-1/2 p-1.5 rounded-full bg-black/40 text-white opacity-0 group-hover:opacity-100 transition-opacity hover:bg-black/60">
                                <CaretLeft size={16} />
                            </button>
                            <button onClick={nextPhoto} className="absolute z-20 right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-full bg-black/40 text-white opacity-0 group-hover:opacity-100 transition-opacity hover:bg-black/60">
                                <CaretRight size={16} />
                            </button>
                            <span className="absolute z-20 bottom-2 right-2 text-[10px] font-semibold px-2 py-0.5 rounded-full"
                                style={{ background: "rgba(5,10,17,0.8)", color: "var(--gold)" }}>
                                {photoIdx + 1} / {photos.length}
                            </span>
                        </>
                    )}
                </div>
                <PhotoLightbox open={lightboxOpen} photos={photos} initialIndex={photoIdx} onClose={() => setLightboxOpen(false)} />
                </>
            )}
            <div className="p-4">
                <div className="flex items-center justify-between gap-2 mb-1">
                    <div className="text-[10px] uppercase tracking-[0.2em] text-white/50">{r.category} · {r.city}</div>
                    <div className="flex items-center gap-2">
                        <div className="flex">
                            {Array.from({ length: 5 }).map((_, i) => (
                                <Star key={i} size={10} weight={i < r.rating ? "fill" : "regular"} color={i < r.rating ? "#D4AF37" : "rgba(255,255,255,0.3)"} />
                            ))}
                        </div>
                        {(r.helpful_count > 0) && (
                            <span className="text-[10px] text-white/40">👍 {r.helpful_count}</span>
                        )}
                    </div>
                </div>
                <div className="font-serif text-lg text-white leading-tight mb-1">{r.place_name}</div>
                <div className="text-sm text-white/70 leading-relaxed mb-2">{r.tip}</div>
                <div className="flex flex-wrap items-center gap-3 text-[10px] uppercase tracking-[0.15em] text-white/50">
                    {r.pricing && <span>{r.pricing}</span>}
                    {r.accessibility && <span className="max-w-full break-words leading-relaxed whitespace-normal">{r.pricing ? "· " : ""}♿ {r.accessibility}</span>}
                    {r.safety && <span className="flex items-center gap-1 max-w-full break-words leading-relaxed whitespace-normal" style={{ color: safetyColor(r.safety) }}>{r.pricing || r.accessibility ? "· " : ""}<ShieldCheck size={12} className="shrink-0" /> {r.safety}</span>}
                </div>
                <div className="flex items-center justify-between mt-3 pt-3 border-t" style={{ borderColor: "rgba(255,255,255,0.06)" }}>
                    <Link to={`/profile/${r.user_handle}`} className="text-xs hover:opacity-80 transition-opacity" style={{ color: "var(--gold)" }}>@{r.user_handle}</Link>
                    <span className="text-[10px] text-white/40 uppercase tracking-widest">{timeAgo(r.created_at)}</span>
                </div>
            </div>
        </div>
    );
}

const Empty = ({ text }) => (
    <div className="text-center py-14 rounded-2xl text-white/50 text-sm col-span-full" style={{ background: "var(--navy-deep)", border: "1px dashed rgba(255,255,255,0.1)" }}>
        {text}
    </div>
);
